import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentEmployee } from '../../services/pinAuthService';
import { getRedemptionHistory } from '../../services/simpleCompService';
import styles from './EmployeeHistory.module.css';

export const EmployeeHistory: React.FC = () => {
  const employee = getCurrentEmployee();
  const [page, setPage] = useState(1);

  const { data: history, isLoading, error } = useQuery({
    queryKey: ['employee-redemption-history', employee?.id, page],
    queryFn: () => employee?.id
      ? getRedemptionHistory(employee.id, page * 10)
      : Promise.resolve({ data: [], error: null }),
    placeholderData: (previousData) => previousData,
  });

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        LOADING HISTORY...
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        Failed to load redemption history. Please try again.
      </div>
    );
  }

  return (
    <div className={styles.history}>
      <div className={styles.header}>
        <h1>REDEMPTION HISTORY</h1>
        <p>All your comp redemptions in one place</p>
      </div>

      {history?.data && history.data.length > 0 ? (
        <div className={styles.historyList}>
          {history.data.map((redemption) => (
            <div key={redemption.id} className={styles.historyItem}>
              <div className={styles.itemHeader}>
                <div className={`${styles.compType} ${redemption.type}`}>
                  {redemption.type === 'food' ? '🍕 FOOD' : '🍹 DRINK'}
                </div>
                <div className={styles.itemTime}>
                  {formatDateTime(redemption.redeemed_at)}
                </div>
              </div>
              <div className={styles.itemDetails}>
                {redemption.notes && (
                  <div className={styles.notes}>
                    <strong>Notes:</strong> {redemption.notes}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📋</div>
          <h3>No redemptions yet</h3>
          <p>You haven't redeemed any comps yet. Start using your QR code at the register!</p>
        </div>
      )}

      {history?.data && history.data.length >= 10 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className={styles.paginationButton}
          >
            ← PREV
          </button>
          <span className={styles.pageInfo}>
            Page {page}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            className={styles.paginationButton}
          >
            NEXT →
          </button>
        </div>
      )}
    </div>
  );
};
